import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Download, Loader2, Printer, QrCode } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select';
import { AdminPage } from '../../../components/design-system/admin/AdminPage';
import { EmptyState } from '../../../components/design-system/admin/EmptyState';
import { SectionCard } from '../../../components/design-system/admin/SectionCard';
import { SegmentedChoice } from '../../../components/design-system/common/SegmentedChoice';
import { SettingsField } from '../../../components/design-system/common/SettingsPanel';
import { useShopperRequestsApi } from '../../../common/hooks/use-shopper-requests-api.hook';
import { useAuthContext } from '../../../common/hooks/use-auth-context.hook';
import { config } from '../../../common/hooks/use-settings-api.hook';
import { Translate, unitLabel } from '../../../modules/vendor/shopper-requests/request-format';
import { ProductPicker } from '../../../modules/vendor/shopper-requests/showroom/ProductPicker';
import { ShowroomSheet } from '../../../modules/vendor/shopper-requests/showroom/ShowroomSheet';
import { SheetCard, buildShowroomPdf, printSheetPages } from '../../../modules/vendor/shopper-requests/showroom/showroom-pdf';
import {
    SHEET_LAYOUTS,
    SheetLayoutKey,
    ShowroomProduct,
    formatPriceWithUnit,
    showroomQrUrl,
} from '../../../modules/vendor/shopper-requests/showroom/showroom-sheet';
import type { VendorStore } from '../../../modules/vendor/shopper-requests/types';

const LANGS = ['en', 'fr', 'es', 'ar'];

/**
 * Sales → Showroom QR codes: a printable card per product on display. A
 * shopper scans it in the showroom and sees the product in their own room;
 * the visit (and any request it leads to) is counted as Showroom QR, and
 * linked to the store when one is chosen.
 */
export const ShowroomQrPage = () => {
    const { t, i18n } = useTranslation();
    const { user } = useAuthContext();
    const api = useShopperRequestsApi();
    const alias = user?.vendor?.alias || config?.alias || '';
    const lang = LANGS.includes((i18n.language || 'en').slice(0, 2)) ? (i18n.language || 'en').slice(0, 2) : 'en';

    const [selected, setSelected] = useState<ShowroomProduct[]>([]);
    const [stores, setStores] = useState<VendorStore[]>([]);
    const [storeId, setStoreId] = useState('');
    const [layoutKey, setLayoutKey] = useState<SheetLayoutKey>('large');
    const [making, setMaking] = useState(false);
    const sheetRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        api.getSettings()
            .then((s) => setStores(s?.stores ?? []))
            .catch(() => undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const store = stores.find((s) => s.id === storeId) ?? null;
    const layout = SHEET_LAYOUTS[layoutKey];

    const translatorFor = useCallback(
        (l: string): Translate => {
            const fixed = i18n.getFixedT(l);
            return (key, options) => String(fixed(key, options as any));
        },
        [i18n],
    );

    const priceIn = useCallback(
        (product: ShowroomProduct, l: string) =>
            product.price ? formatPriceWithUnit(product.price, l, unitLabel(product.price.unit, translatorFor(l))) : null,
        [translatorFor],
    );

    const cardsIn = useCallback(
        (l: string): SheetCard[] =>
            selected.map((p) => ({
                key: p.uuid,
                name: p.name,
                imageUrl: p.imageUrl,
                price: priceIn(p, l),
                url: showroomQrUrl({ alias, sku: p.sku, storeId: store?.id }),
            })),
        [selected, priceIn, alias, store?.id],
    );

    const cards = useMemo(() => cardsIn(lang), [cardsIn, lang]);
    const cta = t('SHOPPER_REQUESTS.SHOWROOM.CTA');

    const toggle = (p: ShowroomProduct) =>
        setSelected((list) => (list.some((x) => x.uuid === p.uuid) ? list.filter((x) => x.uuid !== p.uuid) : [...list, p]));

    const downloadPdf = async () => {
        setMaking(true);
        try {
            // jsPDF's built-in font cannot draw Arabic: an Arabic-speaking vendor
            // gets the PDF's words in English (Print keeps Arabic).
            const pdfLang = lang === 'ar' ? 'en' : lang;
            const { doc, missingImages } = await buildShowroomPdf({
                cards: cardsIn(pdfLang),
                layout,
                cta: translatorFor(pdfLang)('SHOPPER_REQUESTS.SHOWROOM.CTA'),
                storeName: store?.name,
            });
            doc.save(`${t('SHOPPER_REQUESTS.SHOWROOM.FILE_NAME')}${store ? `-${store.id}` : ''}.pdf`);
            if (missingImages) toast(t('SHOPPER_REQUESTS.SHOWROOM.PDF_IMAGES_MISSING'));
        } catch {
            toast.error(t('SHOPPER_REQUESTS.SHOWROOM.PDF_FAILED'));
        } finally {
            setMaking(false);
        }
    };

    const print = () => {
        const pages = Array.from(sheetRef.current?.querySelectorAll<HTMLElement>('[data-sheet-page]') ?? []);
        printSheetPages(pages, t('SHOPPER_REQUESTS.SHOWROOM.TITLE'));
    };

    const ready = cards.length > 0 && !!alias;

    return (
        <AdminPage
            title={t('SHOPPER_REQUESTS.SHOWROOM.TITLE')}
            description={t('SHOPPER_REQUESTS.SHOWROOM.SUBTITLE')}
            parents={[{ label: t('R2R.WORKSPACE.SALES') }]}
            actions={
                <>
                    <Button variant="outline" onClick={print} disabled={!ready} data-testid="sr-showroom-print">
                        <Printer />
                        {t('SHOPPER_REQUESTS.SHOWROOM.PRINT')}
                    </Button>
                    <Button onClick={() => void downloadPdf()} disabled={!ready || making} data-testid="sr-showroom-pdf">
                        {making ? <Loader2 className="animate-spin" /> : <Download />}
                        {t('SHOPPER_REQUESTS.SHOWROOM.DOWNLOAD')}
                    </Button>
                </>
            }
        >
            {!alias && (
                <p className="rounded-xl border p-4 text-sm text-muted-foreground" data-testid="sr-showroom-no-alias">
                    {t('SHOPPER_REQUESTS.SHOWROOM.NO_ALIAS')}
                </p>
            )}

            <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(0,400px)_minmax(0,1fr)]">
                <div className="flex flex-col gap-6">
                    <SectionCard
                        title={t('SHOPPER_REQUESTS.SHOWROOM.PRODUCTS')}
                        description={t('SHOPPER_REQUESTS.SHOWROOM.PRODUCTS_DESC')}
                        action={
                            selected.length > 0 ? (
                                <Button variant="ghost" size="sm" onClick={() => setSelected([])}>
                                    {t('SHOPPER_REQUESTS.SHOWROOM.CLEAR')}
                                </Button>
                            ) : undefined
                        }
                    >
                        <p className="mb-3 text-xs text-muted-foreground" aria-live="polite">
                            {t('SHOPPER_REQUESTS.SHOWROOM.SELECTED', { count: selected.length })}
                        </p>
                        <ProductPicker selected={selected} onToggle={toggle} priceText={(p) => priceIn(p, lang)} />
                    </SectionCard>

                    <SectionCard title={t('SHOPPER_REQUESTS.SHOWROOM.OPTIONS')}>
                        <div className="flex flex-col gap-5">
                            <SettingsField label={t('SHOPPER_REQUESTS.SHOWROOM.STORE')} htmlFor="sr-showroom-store" hint={t('SHOPPER_REQUESTS.SHOWROOM.STORE_HINT')}>
                                <NativeSelect id="sr-showroom-store" className="w-full" value={storeId} onChange={(e) => setStoreId(e.target.value)} data-testid="sr-showroom-store">
                                    <NativeSelectOption value="">{t('SHOPPER_REQUESTS.SHOWROOM.STORE_NONE')}</NativeSelectOption>
                                    {stores.map((s) => (
                                        <NativeSelectOption key={s.id} value={s.id}>
                                            {s.name}
                                        </NativeSelectOption>
                                    ))}
                                </NativeSelect>
                            </SettingsField>
                            <SettingsField label={t('SHOPPER_REQUESTS.SHOWROOM.LAYOUT')}>
                                <SegmentedChoice<SheetLayoutKey>
                                    value={layoutKey}
                                    onChange={setLayoutKey}
                                    options={[
                                        { value: 'large', label: t('SHOPPER_REQUESTS.SHOWROOM.LAYOUT_LARGE'), hint: t('SHOPPER_REQUESTS.SHOWROOM.LAYOUT_LARGE_HINT') },
                                        { value: 'compact', label: t('SHOPPER_REQUESTS.SHOWROOM.LAYOUT_COMPACT'), hint: t('SHOPPER_REQUESTS.SHOWROOM.LAYOUT_COMPACT_HINT') },
                                    ]}
                                />
                            </SettingsField>
                            {lang === 'ar' && <p className="text-xs text-muted-foreground">{t('SHOPPER_REQUESTS.SHOWROOM.PDF_ARABIC_NOTE')}</p>}
                        </div>
                    </SectionCard>
                </div>

                <SectionCard title={t('SHOPPER_REQUESTS.SHOWROOM.PREVIEW')} className="lg:sticky lg:top-4" testId="sr-showroom-sheet">
                    {cards.length ? (
                        <ShowroomSheet
                            ref={sheetRef}
                            cards={cards}
                            layout={layout}
                            cta={cta}
                            storeName={store?.name}
                            dir={lang === 'ar' ? 'rtl' : 'ltr'}
                            pageLabel={(page, total) => t('SHOPPER_REQUESTS.SHOWROOM.PAGE_OF', { page, total })}
                        />
                    ) : (
                        <EmptyState icon={<QrCode />} title={t('SHOPPER_REQUESTS.SHOWROOM.PREVIEW_EMPTY')} description={t('SHOPPER_REQUESTS.SHOWROOM.PREVIEW_EMPTY_BODY')} />
                    )}
                </SectionCard>
            </div>
        </AdminPage>
    );
};

export default ShowroomQrPage;
